import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';

import PersonalInformationAccordionItem from '../../components/authentication/PersonalInformationAccordionItem';
import withAccountLayout from '../../components/account/withAccountLayout';
import AccordionTitle from '../../components/account/AccordionTitle';
import CredentialInformation from '../../components/authentication/CredentialInformation';
import DeleteInformation from '../../components/authentication/DeleteInformation';
import { deleteUser, updateUser, updateUserPassword } from '../../../modules/actions/user';
import { apiClient, useAsyncFunction } from '../../../helpers/apiHelperV2';
import { trackEvent } from '../../components/Tracking/Matomo';
import Button from '../../components/form-elements/Button';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';

const confirmQuestion = 'löschen'; // Falls der Benutzer sich entscheidet sein Konto bei APS zu löschen, so wird dieser gebeten diesen String einzugeben.

const UpdateUserSchema = Yup.object().shape({
    isCompany: Yup.boolean(),
    company: Yup.string().when('isCompany', {
        is: true,
        then: Yup.string().required('Pflichtfeld'),
    }),
    salutation: Yup.string().required('Pflichtfeld'),
    titel: Yup.string(),
    firstName: Yup.string().required('Pflichtfeld'),
    lastName: Yup.string().required('Pflichtfeld'),
    street: Yup.string().required('Pflichtfeld'),
    additionalAddress: Yup.string(),
    zipCode: Yup.string()
        .required('Pflichtfeld')
        .test('zipCode', 'Bitte geben Sie eine gültige deutsche PLZ ein', async function (zipCode) {
            if (zipCode && zipCode.length === 5) {
                try {
                    const {
                        data: { isValid },
                    } = await apiClient.post('/api/v1/validation/zipCode', { zipCode });
                    return isValid;
                } catch (err) {
                    console.error(err);
                    return false;
                }
            }
        }),
    city: Yup.string().required('Pflichtfeld'),
    country: Yup.string(),
    phoneNumber: Yup.string(),
});

const UpdateUserPasswordSchema = Yup.object().shape({
    password: Yup.string().required('Pflichtfeld'),
    confirmPassword: Yup.string()
        .required('Pflichtfeld')
        .test('confirmPassword', 'Passwörter stimmen nicht überein', function (value) {
            return this.parent.password === value;
        }),
});

const DeleteUserSchema = Yup.object().shape({
    password: Yup.string().required('Pflichtfeld'),
    confirmQuestion: Yup.string()
        .required('Pflichtfeld')
        .test('confirmQuestion', 'Sicherheitsabfrage stimmt nicht überein', function (value) {
            return confirmQuestion === value;
        }),
});

const initialValues = {
    isCompany: false,
    company: '',
    salutation: '',
    titel: '',
    firstName: '',
    lastName: '',
    street: '',
    additionalAddress: '',
    zipCode: '',
    city: '',
    country: 'Deutschland',
    phoneNumber: '',
};

function AccountForm({ updateUser, deleteUser, updateUserPassword }) {
    const usersResponse = useAsyncFunction(async () => await apiClient.get(`/api/v1/users`, { authenticated: true }), []);

    const handleUpdateUserSubmit = async (
        { isCompany, company, salutation, titel, firstName, lastName, street, additionalAddress, zipCode, city, country, phoneNumber },
        actions
    ) => {
        const user = {
            isCompany,
            company,
            salutation,
            titel,
            firstName,
            lastName,
            street,
            additionalAddress,
            zipCode,
            city,
            country,
            phoneNumber,
        };
        await updateUser({ user });
        actions.setSubmitting(false);
    };

    const handleUpdateCredentialsSubmit = async ({ password }, actions) => {
        await updateUserPassword({ password });
        actions.setSubmitting(false);
    };

    const handleDeleteUserSubmit = async ({ password, confirmQuestion }, actions) => {
        trackEvent({ category: 'Account', action: 'submit-delete-account', label: 'Konto löschen' });
        await deleteUser({ password });
        actions.setSubmitting(false);
    };

    return (
        <div className="uk-container">
            <ul data-uk-accordion="multiple: true">
                <li className="uk-open">
                    <AccordionTitle>
                        <div>Persönliche Informationen</div>
                    </AccordionTitle>
                    <div className="uk-accordion-content">
                        <div className="uk-grid-divider uk-grid-small uk-child-width-1-1" data-uk-grid="">
                            <div>
                                <ReduxSuspense response={usersResponse}>
                                    {data => (
                                        <Formik
                                            initialValues={{ ...initialValues, ...data?.data?.user }}
                                            validationSchema={UpdateUserSchema}
                                            onSubmit={handleUpdateUserSubmit}
                                            render={({ values, setFieldValue, errors, touched, handleBlur }) => {
                                                return (
                                                    <Form className="uk-form">
                                                        <ul data-uk-accordion="multiple: true">
                                                            <PersonalInformationAccordionItem
                                                                setFieldValue={setFieldValue}
                                                                handleBlur={handleBlur}
                                                                errors={errors}
                                                                touched={touched}
                                                                values={values}
                                                            />
                                                            <Button type="submit" fullWidth>
                                                                Meine persönlichen Informationen aktualisieren
                                                            </Button>
                                                        </ul>
                                                    </Form>
                                                );
                                            }}
                                        />
                                    )}
                                </ReduxSuspense>
                            </div>
                        </div>
                    </div>
                </li>
                <li className="uk-open">
                    <AccordionTitle>
                        <div>Kundenkonto Informationen</div>
                    </AccordionTitle>
                    <div className="uk-accordion-content">
                        <div className="uk-grid-divider uk-grid-small uk-child-width-1-1" data-uk-grid="">
                            <Formik
                                initialValues={{ password: '', confirmPassword: '' }}
                                validationSchema={UpdateUserPasswordSchema}
                                onSubmit={handleUpdateCredentialsSubmit}
                                render={() => {
                                    return (
                                        <Form className="uk-form">
                                            <ul data-uk-accordion="multiple: true">
                                                <CredentialInformation counter="2" title="Passwort ändern" hideEmailField={true} />
                                                <Button type="submit" fullWidth>
                                                    Mein Passwort aktualisieren
                                                </Button>
                                            </ul>
                                        </Form>
                                    );
                                }}
                            />
                            <Formik
                                initialValues={{ password: '', confirmQuestion: '' }}
                                validationSchema={DeleteUserSchema}
                                onSubmit={handleDeleteUserSubmit}
                                render={() => {
                                    return (
                                        <Form className="uk-form">
                                            <ul data-uk-accordion="multiple: true">
                                                <DeleteInformation confirmQuestion={confirmQuestion} />
                                                <Button type="submit" modifier="secondary" fullWidth>
                                                    Mein Kundenkonto löschen
                                                </Button>
                                            </ul>
                                        </Form>
                                    );
                                }}
                            />
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    );
}

const mapDispatchToProps = { updateUser, deleteUser, updateUserPassword };

export default connect(
    null,
    mapDispatchToProps
)(withAccountLayout(AccountForm, { title: 'Meine Einstellungen', hideLocationSelector: true }));
